import { Fragment, useState } from 'react';
import type { Deal, TabState } from '../../types';
import { buildSCList, PRODUCT_BUCKETS, bucketProducts, matchesCQFilter } from '../../utils/filter';
import { fmtG } from '../../utils/format';
import { sumUniqueBy, validAges, isAged, ageSeverity, formatAge, splitSCNames } from '../../utils/deals';
import { useDeals } from '../../context/DealsContext';
import { MetricCard } from '../ui/MetricCard';
import { MedianAgeCard } from '../ui/MedianAgeCard';
import { GnarrInfluencedCard } from '../ui/GnarrInfluencedCard';
import { SectionHeader } from '../ui/SectionHeader';
import { getURL } from '../../utils/deals';
interface Props {
  state: TabState;
}
function SCMatrix({
  region,
  sub,
  cqState,
  data
}: {
  region: string;
  sub: string;
  cqState: TabState;
  data: Deal[];
}) {
  const ipData = data.filter(d => d.status === 'In Progress' && (region === 'Global' || d.region === region) && (!sub || sub === 'All' || d.sub_region === sub) && matchesCQFilter(d, cqState));
  const COLS = [...PRODUCT_BUCKETS.map(b => b.label), 'Other'];
  const scMap: Record<string, Record<string, number>> = {};
  const seenDR: Record<string, number> = {};
  for (const d of ipData) {
    const bkts = bucketProducts(d.products || '');
    for (const scName of splitSCNames(d.sc)) {
      if (!scMap[scName]) scMap[scName] = {};
      for (const bkt of bkts) {
        const uniq = scName + '|' + bkt + '|' + d.id;
        if (seenDR[uniq]) continue;
        seenDR[uniq] = 1;
        scMap[scName][bkt] = (scMap[scName][bkt] || 0) + 1;
      }
    }
  }
  const scRows = Object.entries(scMap).map(([name, bkts]) => ({
    name: name.split(' ')[0],
    full: name,
    bkts,
    total: Object.values(bkts).reduce((a, b) => a + b, 0)
  })).sort((a, b) => b.total - a.total);
  const colTotals: Record<string, number> = {};
  for (const c of COLS) colTotals[c] = 0;
  for (const row of scRows) {
    for (const c of COLS) colTotals[c] += row.bkts[c] || 0;
  }
  const grandTotal = Object.values(colTotals).reduce((a, b) => a + b, 0);
  const activeCols = COLS.filter(c => colTotals[c] > 0);
  if (scRows.length === 0 || grandTotal === 0) {
    return <tr data-id="element-205" data-mp="3q3zlf1xxjqhn">
        <td colSpan={2} style={{
        padding: '12px 10px',
        color: '#8892a0',
        fontSize: 10,
        fontStyle: 'italic'
      }} data-id="element-206" data-mp="3q3g49ng3t64o">
          No in-progress deals match the current filter.
        </td>
      </tr>;
  }
  return <>
      <thead data-id="element-207" data-mp="2xgd995amzdj6">
        <tr data-id="element-208" data-mp="2xglp96222yyl">
          <th data-id="element-209" data-mp="2xh55mic25fai">SC</th>
          {activeCols.map(c => <th key={c} data-id="element-210" data-mp="32e59cmlfq5yg">{c}</th>)}
          <th data-id="element-211" data-mp="32fgbkhz8f5ng">Total</th>
        </tr>
      </thead>
      <tbody data-id="element-212" data-mp="32c115sgi6ksd">
        {scRows.map(row => <tr key={row.full} data-id="element-213" data-mp="36exjdi24sevs">
            <td title={row.full} data-id="element-214" data-mp="36ep4xlqd11ih">{row.name}</td>
            {activeCols.map(c => <td key={c} data-id="element-215" data-mp="3b89vispmit7n">
                {(row.bkts[c] || 0) > 0 ? <span className="mx-val" data-id="element-216" data-mp="2epm28ae4fz7y">{row.bkts[c]}</span> : <span style={{
            color: '#d1d5db'
          }} data-id="element-217" data-mp="2iqedtaagug7e">—</span>}
              </td>)}
            <td data-id="element-218" data-mp="2io1mi6ititho"><span className="mx-val" style={{
            background: '#e0e7ff',
            color: '#3730a3'
          }} data-id="element-219" data-mp="2b6wpzkq6v451">{row.total}</span></td>
          </tr>)}
      </tbody>
      <tfoot data-id="element-220" data-mp="2693lcw4jai6v">
        <tr data-id="element-221" data-mp="268bntfrdmady">
          <td data-id="element-222" data-mp="265pi7lo1q4yd">Total</td>
          {activeCols.map(c => <td key={c} data-id="element-223" data-mp="2255l2i3h31c8"><span style={{
            fontWeight: 700,
            color: '#1d4ed8'
          }} data-id="element-224" data-mp="224vtiq2s4m4f">{colTotals[c] || 0}</span></td>)}
          <td data-id="element-225" data-mp="1x6lycfh21lit"><span style={{
            fontWeight: 700,
            color: '#3730a3'
          }} data-id="element-226" data-mp="1x6lwsb1epdgp">{grandTotal}</span></td>
        </tr>
      </tfoot>
    </>;
}
export function ODPanel({
  state
}: Props) {
  const [expandedSC, setExpandedSC] = useState<string | null>(null);
  const {
    deals
  } = useDeals();
  const scData = buildSCList(state.r, state.sub, state, deals);
  const allIP = deals.filter(d => {
    if (d.status !== 'In Progress') return false;
    if (state.r !== 'Global' && d.region !== state.r) return false;
    if (state.sub && state.sub !== 'All' && d.sub_region !== state.sub) return false;
    if (!matchesCQFilter(d, state)) return false;
    return true;
  });
  const scs = new Set(allIP.flatMap(d => splitSCNames(d.sc))).size;

  // oppGnarr includes ALL statuses (not just IP), matching region/sub/CQ
  const allForGnarr = deals.filter(d => {
    if (state.r !== 'Global' && d.region !== state.r) return false;
    if (state.sub && state.sub !== 'All' && d.sub_region !== state.sub) return false;
    if (!matchesCQFilter(d, state)) return false;
    return true;
  });
  const oppGnarr = sumUniqueBy(allForGnarr, d => d.opp_gnarr);
  const ages = validAges(allIP);
  const maxIP = Math.max(...scData.map(s => s.ip), 1);
  const maxClosed = Math.max(...scData.map(s => s.closed), 1);
  return <div data-id="element-227" data-mp="2bxleaz6t8ykc">
      {/* Metrics */}
      <div className="mrow" data-id="element-228" data-mp="2gv3p6v4e1nuz">
        <MetricCard label="In-progress requests" value={allIP.length} sub={`${scs} SCs active`} colorClass="c-blue" data-id="element-229" data-mp="2gubsti2yvm3n" />
        <MetricCard label="Requests / person" value={scData.length ? +(allIP.length / scData.length).toFixed(1) : 0} sub="across active SCs" colorClass="c-purple" data-id="element-230" data-mp="a5yv3jt5u7yf" />
        <MedianAgeCard ages={ages} data-id="element-231" data-mp="1zlj957ktd1vc" />
        <GnarrInfluencedCard value={oppGnarr} colorClass="c-green" data-id="element-232" data-mp="1zmkovpcrycje" />
      </div>

      {/* SC workload table */}
      <SectionHeader title="SC workload — deals (click row to expand)" data-id="element-233" data-mp="1uo9ad1bjbduu" />
      <div className="tw" data-id="element-234" data-mp="29cnpaam86srr">
        <table className="tbl" data-id="element-235" data-mp="29cx5j6hgxlk9">
          <thead data-id="element-236" data-mp="24kvz416zep25">
            <tr data-id="element-237" data-mp="24kccw8aateib">
              <th style={{
              width: '26%'
            }} data-id="element-238" data-mp="24jujrkixngem">SC Name</th>
              <th style={{
              width: '18%'
            }} data-id="element-239" data-mp="2icagk3l8qi73">In-progress</th>
              <th style={{
              width: '18%'
            }} data-id="element-240" data-mp="2iaplmcv0kx8p">Closed</th>
              <th style={{
              width: '13%'
            }} data-id="element-241" data-mp="2ddfy52r6qvni">Avg age</th>
              <th style={{
              width: '13%'
            }} data-id="element-242" data-mp="2dcxuhl271f5k">Median age</th>
              <th style={{
              width: '12%'
            }} data-id="element-243" data-mp="2saq8u9w07xi6">GNARR (In-Progress)</th>
            </tr>
          </thead>
          <tbody data-id="element-244" data-mp="2nb3v7zc9kf8u">
            {scData.map((p, rowPosition) => <Fragment key={`${p.n}-${rowPosition}`} data-id="element-245" data-mp="2n9ix5zqqqe68" data-mpi={rowPosition}>
                <tr key={p.n} onClick={() => setExpandedSC(expandedSC === p.n ? null : p.n)} data-id="element-246" data-mp="xx8ikf47smjg">
                  <td data-id="element-247" data-mp="syns37erlh87">
                    <span className="name-link" data-id="element-248" data-mp="sz6ybqv1yh6e">
                      {p.n}
                      <span className={`chev${expandedSC === p.n ? ' open' : ''}`} data-id="element-249" data-mp="1imt620h98pjv">▾</span>
                    </span>
                  </td>
                  <td data-id="element-250" data-mp="1mowjgunhi47s">
                    <div className="pbg" style={{
                  width: 70
                }} data-id="element-251" data-mp="1rln13mext7vb">
                      <div className="pf" style={{
                    width: `${Math.round(p.ip / maxIP * 100)}%`
                  }} data-id="element-252" data-mp="1ro98nm1tvndj" />
                    </div>
                    <strong data-id="element-253" data-mp="21klyg8u95h30">{p.ip}</strong>
                  </td>
                  <td data-id="element-254" data-mp="21k3x4xsqgco8">
                    <div className="pbg" style={{
                  width: 70
                }} data-id="element-255" data-mp="25lxr6n8faiff">
                      <div className="pf" style={{
                    width: `${Math.round(p.closed / maxClosed * 100)}%`,
                    background: '#34d399'
                  }} data-id="element-256" data-mp="25k4k56twdw6w" />
                    </div>
                    <strong data-id="element-257" data-mp="2fca9e8srlzie">{p.closed}</strong>
                  </td>
                  <td style={{
                color: p.avgAge > 90 ? '#f87171' : p.avgAge > 40 ? '#d4a017' : '#344054',
                fontWeight: p.avgAge > 40 ? 600 : 400
              }} data-id="element-258" data-mp="2fdldk9q4h9a1">
                    {p.avgAge}d
                  </td>
                  <td data-id="element-259" data-mp="dml8czxypzkh">{p.med}d</td>
                  <td data-id="element-260" data-mp="dmuxklb6o2p4">{fmtG(p.gnarr)}</td>
                </tr>
                {expandedSC === p.n && <tr key={`exp-${p.n}`} data-id="element-261" data-mp="8mgp525o6qqk">
                    <td colSpan={6} style={{
                padding: 0
              }} data-id="element-262" data-mp="8pl2oq50yytw">
                      <div className="exp-inner" data-id="element-263" data-mp="3pgml1814647">
                        <div style={{
                    fontSize: 9,
                    fontWeight: 700,
                    color: '#667085',
                    marginBottom: 6,
                    textTransform: 'uppercase',
                    letterSpacing: '.05em'
                  }} data-id="element-264" data-mp="3uuq21yyed1zk">
                          In-progress book — {p.n} ({p.ip} deals · {fmtG(p.gnarr)} GNARR)
                        </div>
                        <div className="deal-grid" data-id="element-265" data-mp="rerl0c1wpof6">
                          {p.deals.map(d => <div key={d.id} className="deal-chip" data-id="element-266" data-mp="ml6mmiweisfg">
                              <div className="dc-id" data-id="element-267" data-mp="hnew9tbenedd">{d.id}</div>
                              <div className="dc-name" data-id="element-268" data-mp="1f3amo8qgb2ya">{d.account.substring(0, 35)}</div>
                              <div style={{
                        fontSize: 9,
                        color: '#8892a0',
                        marginTop: 3
                      }} data-id="element-269" data-mp="1acu82law9bac"><a href={getURL(d)} target="_blank" rel="noopener noreferrer" data-id="element-270" data-mp="1abasyy0z56a5">{d.requestName.substring(0, 35)}</a></div>
                              <div className="dc-tags" data-id="element-271" data-mp="nfzeugwga7ar">
                                <span className={`badge ${ageSeverity(d.age) === 'high' ? 'b-red' : ageSeverity(d.age) === 'medium' ? 'b-amber' : 'b-gray'}`} data-id="element-272" data-mp="nhkak9ui3wa7">{formatAge(d.age)}</span>
                                <span className="badge b-blue" data-id="element-273" data-mp="eoqr0080uaqj">{fmtG(d.gnarr)}</span>
                                <span className="badge b-purple" style={{
                          fontSize: 8
                        }} data-id="element-274" data-mp="12d09as610gcx">{d.close_q}</span>
                                {isAged(d) && <span className="badge b-red" data-id="element-275" data-mp="16dk6fvqlnjz2">⚠ aged</span>}
                              </div>
                              <div style={{
                        fontSize: 9,
                        color: '#8892a0',
                        marginTop: 3
                      }} data-id="element-276" data-mp="sekfghfm6szq">
                                {d.sub_region} · {d.offering.substring(0, 26)}
                              </div>
                            </div>)}
                        </div>
                        {p.closedDeals.length > 0 && <>
                            <div style={{
                      fontSize: 9,
                      fontWeight: 700,
                      color: '#059669',
                      margin: '12px 0 6px',
                      textTransform: 'uppercase',
                      letterSpacing: '.05em'
                    }} data-id="element-277" data-mp="3ajk8f621zzxu">
                              Closed — {p.n} ({p.closed} deals)
                            </div>
                            <div className="deal-grid" data-id="element-278" data-mp="3emqdwbhcdn39">
                              {p.closedDeals.map(d => <div key={d.id} className="deal-chip" style={{
                        borderColor: '#d1fae5'
                      }} data-id="element-279" data-mp="2qw4gde4yrl7i">
                                  <div className="dc-id" data-id="element-280" data-mp="2mwc3ip8jeswc">{d.id}</div>
                                  <div className="dc-name" data-id="element-281" data-mp="30mfjiygtpqdc">{d.account.substring(0, 35)}</div>
                                  <div style={{
                          fontSize: 9,
                          color: '#8892a0',
                          marginTop: 3
                        }} data-id="element-282" data-mp="2vvz81jwkcetm"> <a href={getURL(d)} target="_blank" rel="noopener noreferrer" data-id="element-283" data-mp="2vufrrtawq3ru">{d.requestName.substring(0, 40)}</a></div>
                                  <div className="dc-tags" data-id="element-284" data-mp="237bel11nuj4z">
                                    <span className="badge" style={{
                            background: '#d1fae5',
                            color: '#065f46'
                          }} data-id="element-285" data-mp="236s76e9mzd57">✓ Closed</span>
                                    <span className="badge b-blue" data-id="element-286" data-mp="9k3ab4j3ircr">{fmtG(d.gnarr)}</span>
                                    <span className="badge b-purple" style={{
                            fontSize: 8
                          }} data-id="element-287" data-mp="4l9v97o5usah">{d.close_q}</span>
                                  </div>
                                  <div style={{
                          fontSize: 9,
                          color: '#8892a0',
                          marginTop: 3
                        }} data-id="element-288" data-mp="3rsu2b5k04rix">
                                    {d.sub_region} · {d.offering.substring(0, 26)}
                                  </div>
                                </div>)}
                            </div>
                          </>}
                      </div>
                    </td>
                  </tr>}
              </Fragment>)}
          </tbody>
        </table>
      </div>

      {/* Solution × SC matrix */}
      <SectionHeader title="Solution × SC matrix — product concentration" data-id="element-289" data-mp="2z3n2m38t9vw3" />
      <div className="tw" data-id="element-290" data-mp="2z5r8gqq9t4z0">
        <div className="sub" data-id="element-291" data-mp="1elvvjaqac00i">
          In-progress deals by product bucket per SC. Reveals single-threading and coverage gaps.
        </div>
        <div style={{
        overflowX: 'auto'
      }} data-id="element-292" data-mp="1jiw0tjfb8yqk">
          <table className="tbl" id="od-matrix-tbl" style={{
          minWidth: 500
        }} data-id="element-293" data-mp="1nmahmcb2pj52">
            <SCMatrix region={state.r} sub={state.sub} cqState={state} data={deals} data-id="element-294" data-mp="1nk7v4294q853" />
          </table>
        </div>
      </div>
    </div>;
}